import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Box, Button, IconButton, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import {
  buildUtmGeneratorValuesFromUrl,
  extractQueryParamsFromUrl,
  getUsedUtmKeys,
  mergeQueryParamsIntoUrl,
  mergeUtmValuesIntoUrl,
  UTM_GENERATOR_KEYS,
  UtmGeneratorKey,
  UtmGeneratorValues,
} from '../../../../utils/urlUtils'
import ToggleDisplay from '../../../../components/layout/collapsible/ShowMoreButtonToggle'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import { FormDialog } from '../../../../components/layout/FormDialog'
import SectionTitle from '../../../../components/form/SectionTitle'
import theme from '../../../../styles/theme'
import { Section } from '../../../../components/layout/Section'

interface RedirectUrlUtmGeneratorDialogProps {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  redirectUrl: string
  onApply: (generatedUrl: string) => void
}

const DEFAULT_UTM_GENERATOR_VALUES: UtmGeneratorValues = {
  utm_source: 'powerspace',
  utm_medium: 'referral',
  utm_campaign: '{campaignName}',
  utm_id: '{adGroupId}',
  utm_term: '{pubName}_{siteName}',
  utm_content: '{adCopyId}_{adCopyName}',
}

const serverTokenDocumentationByGroup: Array<{
  groupName: string
  entries: Array<{ token: string; descriptionKey: string }>
}> = [
  {
    groupName: 'Advertiser info',
    entries: [
      { token: '{campaignId}', descriptionKey: 'campaignId' },
      { token: '{campaignName}', descriptionKey: 'campaignName' },
      { token: '{adGroupId}', descriptionKey: 'adGroupId' },
      { token: '{adGroupName}', descriptionKey: 'adGroupName' },
      { token: DEFAULT_UTM_GENERATOR_VALUES.utm_content, descriptionKey: 'adcopyInfos' },
      { token: '{adCopyId}', descriptionKey: 'adCopyId' },
      { token: '{adCopyName}', descriptionKey: 'adCopyName' },
      { token: '{clickId}', descriptionKey: 'clickId' },
    ],
  },
  {
    groupName: 'Publisher info',
    entries: [
      { token: DEFAULT_UTM_GENERATOR_VALUES.utm_term, descriptionKey: 'publisherInfos' },
      { token: '{pubName}', descriptionKey: 'pubName' },
      { token: '{siteName}', descriptionKey: 'siteName' },
      { token: '{posName}', descriptionKey: 'posName' },
    ],
  },
]

const serverTokenDocumentation = serverTokenDocumentationByGroup.flatMap(({ entries }) => entries)

type CustomParamValueMode = 'custom' | 'dynamicValue'
interface CustomParameter {
  id: string
  key: string
  value: string
  valueMode: CustomParamValueMode
}

const dynamicValues = serverTokenDocumentation.map(({ token }) => token)

const isUtmKey = (key: string): boolean => UTM_GENERATOR_KEYS.includes(key.toLowerCase() as UtmGeneratorKey)

type UtmValueModes = Record<UtmGeneratorKey, CustomParamValueMode>

const buildUtmValueModes = (utmValues: UtmGeneratorValues): UtmValueModes =>
  UTM_GENERATOR_KEYS.reduce<UtmValueModes>((acc, key) => {
    acc[key] = dynamicValues.includes(utmValues[key]) ? 'dynamicValue' : 'custom'
    return acc
  }, {} as UtmValueModes)

const buildCustomParametersFromUrl = (url: string): CustomParameter[] => {
  const queryParams = extractQueryParamsFromUrl(url)
  return Object.entries(queryParams)
    .filter(([key]) => !isUtmKey(key))
    .map(([key, value], index) => ({
      id: `${key}-${index}`,
      key,
      value,
      valueMode: dynamicValues.includes(value) ? 'dynamicValue' : 'custom',
    }))
}

const RedirectUrlUtmGeneratorDialog: React.FC<RedirectUrlUtmGeneratorDialogProps> = ({
  open,
  setOpen,
  redirectUrl,
  onApply,
}) => {
  const t = useAppTranslations()
  const initialUtmValues = buildUtmGeneratorValuesFromUrl(redirectUrl, DEFAULT_UTM_GENERATOR_VALUES)
  const initialUsedUtmKeys = getUsedUtmKeys(redirectUrl)
  const [showDocumentation, setShowDocumentation] = useState(false)
  const [utmValues, setUtmValues] = useState<UtmGeneratorValues>(() => initialUtmValues)
  const [utmValueModes, setUtmValueModes] = useState<UtmValueModes>(() => buildUtmValueModes(initialUtmValues))
  const [activeUtmKeys, setActiveUtmKeys] = useState<UtmGeneratorKey[]>(() => initialUsedUtmKeys)
  const [customParameters, setCustomParameters] = useState<CustomParameter[]>(() =>
    buildCustomParametersFromUrl(redirectUrl)
  )

  const generatedUrl = useMemo(() => {
    const utmValuesFromUrl = activeUtmKeys.reduce<Partial<UtmGeneratorValues>>((acc, key) => {
      acc[key] = utmValues[key]
      return acc
    }, {})
    const urlWithUtm = mergeUtmValuesIntoUrl(redirectUrl, utmValuesFromUrl)
    const customValues = customParameters.reduce<Record<string, string>>((acc, param) => {
      const key = param.key.trim()
      if (!key) return acc
      acc[key] = param.value
      return acc
    }, {})
    const customKeysFromCurrentUrl = Object.keys(extractQueryParamsFromUrl(redirectUrl)).filter((key) => !isUtmKey(key))
    return mergeQueryParamsIntoUrl(urlWithUtm, customValues, customKeysFromCurrentUrl)
  }, [redirectUrl, activeUtmKeys, utmValues, customParameters])

  useEffect(() => {
    if (!open) return

    const nextUtmValues = buildUtmGeneratorValuesFromUrl(redirectUrl, DEFAULT_UTM_GENERATOR_VALUES)
    setUtmValues(nextUtmValues)
    setUtmValueModes(buildUtmValueModes(nextUtmValues))
    setActiveUtmKeys(getUsedUtmKeys(redirectUrl))
    setCustomParameters(buildCustomParametersFromUrl(redirectUrl))
    setShowDocumentation(false)
  }, [redirectUrl, open])

  const closeDialog = (): void => setOpen(false)

  const handleUtmFieldChange = (key: UtmGeneratorKey, value: string): void => {
    setUtmValues((previousValues) => ({ ...previousValues, [key]: value }))
  }

  const handleUtmValueModeChange = (key: UtmGeneratorKey, valueMode: CustomParamValueMode): void => {
    setUtmValueModes((previousModes) => ({ ...previousModes, [key]: valueMode }))
    setUtmValues((previousValues) => ({
      ...previousValues,
      [key]:
        valueMode === 'dynamicValue'
          ? serverTokenDocumentation[0].token
          : previousValues[key] === serverTokenDocumentation[0].token
            ? ''
            : previousValues[key],
    }))
  }

  const addCustomParameter = (): void => {
    setCustomParameters((previousValues) => [
      ...previousValues,
      {
        id: `custom-${Date.now()}`,
        key: '',
        value: '',
        valueMode: 'custom',
      },
    ])
  }

  const removeCustomParameter = (id: string): void => {
    setCustomParameters((previousValues) => previousValues.filter((param) => param.id !== id))
  }

  const updateCustomParameter = (id: string, updates: Partial<CustomParameter>): void => {
    setCustomParameters((previousValues) =>
      previousValues.map((param) => (param.id === id ? Object.assign({}, param, updates) : param))
    )
  }

  const handleGenerate = (): void => {
    onApply(generatedUrl)
    setOpen(false)
  }

  const initializeDefaultUtmKeys = (): void => {
    setActiveUtmKeys([...UTM_GENERATOR_KEYS])
  }

  return (
    <FormDialog open={open} setOpen={setOpen} title={t('adgroup.redirectUrlUtmGenerator.title')} maxWidth="md">
      <TextField
        label={t('adgroup.redirectUrl')}
        value={generatedUrl}
        size="small"
        multiline
        minRows={1}
        fullWidth
        slotProps={{
          input: { readOnly: true },
        }}
      />
      <Section sx={(theme) => ({ mt: theme.spacing(2) })}>
        <SectionTitle title="adgroup.redirectUrlUtmGenerator.utmParameters" withFormik={false} sx={{ mb: 1 }}>
          {activeUtmKeys.length > 0 && (
            <Button variant="text" onClick={initializeDefaultUtmKeys} size="small">
              {t('adgroup.redirectUrlUtmGenerator.resetDefaultUtm')}
            </Button>
          )}
          {activeUtmKeys.length === 0 && (
            <Button variant="text" onClick={initializeDefaultUtmKeys} size="small">
              {t('adgroup.redirectUrlUtmGenerator.initializeDefaultUtm')}
            </Button>
          )}
        </SectionTitle>

        <Stack spacing={1}>
          {activeUtmKeys.map((utmKey) => (
            <Stack
              key={utmKey}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              sx={{
                alignItems: { md: 'center' },
              }}
            >
              <TextField
                label={t('commons.name')}
                value={utmKey}
                size="small"
                sx={{ flex: { md: '0 0 220px' } }}
                slotProps={{
                  input: { readOnly: true },
                }}
              />
              <Select
                value={utmValueModes[utmKey]}
                size="small"
                onChange={(event) => handleUtmValueModeChange(utmKey, event.target.value as CustomParamValueMode)}
                sx={{ flex: { md: '0 0 170px' } }}
              >
                <MenuItem value="custom">{t('adgroup.redirectUrlUtmGenerator.customValue')}</MenuItem>
                <MenuItem value="dynamicValue">{t('adgroup.redirectUrlUtmGenerator.dynamicValue')}</MenuItem>
              </Select>
              {utmValueModes[utmKey] === 'dynamicValue' ? (
                <Select
                  label={t('adgroup.redirectUrlUtmGenerator.valueType')}
                  value={utmValues[utmKey] || serverTokenDocumentation[0].token}
                  size="small"
                  onChange={(event) => handleUtmFieldChange(utmKey, event.target.value as string)}
                  sx={{ flex: 1, minWidth: { md: 280 } }}
                >
                  {serverTokenDocumentation.map(({ token, descriptionKey }) => (
                    <MenuItem key={token} value={token}>
                      {t(`adgroup.redirectUrlUtmGenerator.serverTokens.descriptions.${descriptionKey}`)}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <TextField
                  label={t('adgroup.redirectUrlUtmGenerator.parameterValue')}
                  value={utmValues[utmKey]}
                  size="small"
                  onChange={(event) => handleUtmFieldChange(utmKey, event.target.value)}
                  sx={{ flex: 1 }}
                />
              )}
            </Stack>
          ))}
          {activeUtmKeys.length === 0 && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              sx={{
                alignItems: { md: 'center' },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('adgroup.redirectUrlUtmGenerator.noUtmFound')}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Section>
      <Section>
        <SectionTitle title="adgroup.redirectUrlUtmGenerator.customParameters" withFormik={false} sx={{ mb: 1 }}>
          <Button variant="text" onClick={addCustomParameter} size="small">
            {t('adgroup.redirectUrlUtmGenerator.addParameter')}
          </Button>
        </SectionTitle>

        <Stack spacing={1}>
          {customParameters.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('adgroup.redirectUrlUtmGenerator.noCustomParameters')}
            </Typography>
          )}
          {customParameters.map((param) => (
            <Stack
              key={param.id}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              sx={{
                alignItems: { md: 'center' },
              }}
            >
              <TextField
                label={t('commons.name')}
                value={param.key}
                size="small"
                onChange={(event) => updateCustomParameter(param.id, { key: event.target.value })}
                sx={{ flex: { md: '0 0 220px' } }}
              />
              <Select
                value={param.valueMode}
                size="small"
                onChange={(event) =>
                  updateCustomParameter(param.id, {
                    valueMode: event.target.value as CustomParamValueMode,
                    value:
                      event.target.value === 'dynamicValue'
                        ? serverTokenDocumentation[0].token
                        : param.value === serverTokenDocumentation[0].token
                          ? ''
                          : param.value,
                  })
                }
                sx={{ flex: { md: '0 0 170px' } }}
              >
                <MenuItem value="custom">{t('adgroup.redirectUrlUtmGenerator.customValue')}</MenuItem>
                <MenuItem value="dynamicValue">{t('adgroup.redirectUrlUtmGenerator.dynamicValue')}</MenuItem>
              </Select>
              {param.valueMode === 'dynamicValue' ? (
                <Select
                  value={param.value || serverTokenDocumentation[0].token}
                  size="small"
                  onChange={(event) => updateCustomParameter(param.id, { value: event.target.value as string })}
                  sx={{ flex: 1, minWidth: { md: 280 } }}
                >
                  {serverTokenDocumentation.map(({ token, descriptionKey }) => (
                    <MenuItem key={token} value={token}>
                      {t(`adgroup.redirectUrlUtmGenerator.serverTokens.descriptions.${descriptionKey}`)}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <TextField
                  label={t('adgroup.redirectUrlUtmGenerator.parameterValue')}
                  value={param.value}
                  size="small"
                  onChange={(event) => updateCustomParameter(param.id, { value: event.target.value })}
                  sx={{ flex: 1 }}
                />
              )}
              <IconButton color="error" size="small" onClick={() => removeCustomParameter(param.id)}>
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Section>
      <Box sx={{ mt: theme.spacing(3) }}>
        <ToggleDisplay handleClick={() => setShowDocumentation((prev) => !prev)} isDisplayed={showDocumentation}>
          {t('adgroup.redirectUrlUtmGenerator.documentation')}
        </ToggleDisplay>

        {showDocumentation && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: theme.spacing(2) }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('adgroup.redirectUrlUtmGenerator.serverTokens.title')}
              </Typography>
              <Stack spacing={1}>
                {serverTokenDocumentationByGroup.map(({ groupName, entries }) => (
                  <Box key={groupName}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {groupName === 'Advertiser info'
                        ? t('adgroup.redirectUrlUtmGenerator.serverTokens.groups.advertiserInfo')
                        : t('adgroup.redirectUrlUtmGenerator.serverTokens.groups.publisherInfo')}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {entries.map(({ token, descriptionKey }) => (
                        <Typography
                          key={token}
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          <code>{token}</code> -{' '}
                          {t(`adgroup.redirectUrlUtmGenerator.serverTokens.descriptions.${descriptionKey}`)}
                        </Typography>
                      ))}
                    </Stack>
                    {groupName === 'Advertiser info' && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                          mt: 0.5,
                        }}
                      >
                        {t('adgroup.redirectUrlUtmGenerator.serverTokens.clickIdAutoNote')}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('adgroup.redirectUrlUtmGenerator.defaultUtmValues')}
              </Typography>
              <Stack spacing={0.5}>
                {Object.entries(DEFAULT_UTM_GENERATOR_VALUES).map(([key, value]) => (
                  <Typography
                    key={key}
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    <code>{key}</code> = <code>{value}</code>
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: 'flex-end',
          pt: 1,
        }}
      >
        <Button color="inherit" onClick={closeDialog}>
          {t('commons.actions.cancel')}
        </Button>
        <Button color="primary" onClick={handleGenerate} variant="contained">
          {t('adgroup.redirectUrlUtmGenerator.applyToRedirectUrl')}
        </Button>
      </Stack>
    </FormDialog>
  )
}

export default RedirectUrlUtmGeneratorDialog
